const DEFAULT_TIMEOUT_MS = 800;
const TCF_LOCATOR = '__tcfapiLocator';
let initializedOptions;
let latestTcData;
let latestSource = 'fallback';
let managerUnsubscribe;
const subscribers = new Set();
function debugLog(message, payload) {
    if (!(initializedOptions === null || initializedOptions === void 0 ? void 0 : initializedOptions.debug)) {
        return;
    }
    if (payload === undefined) {
        console.log(`[TCF] ${message}`);
    }
    else {
        console.log(`[TCF] ${message}`, payload);
    }
}
function getTcfApiWindow() {
    let currentWindow = window;
    while (currentWindow) {
        try {
            if (currentWindow.frames[TCF_LOCATOR]) {
                return currentWindow;
            }
        }
        catch (error) {
            // Ignore cross-origin access errors while traversing parents.
        }
        if (currentWindow === window.top) {
            break;
        }
        currentWindow = currentWindow.parent;
    }
    return null;
}
function getTcfApi() {
    const api = window.__tcfapi;
    return typeof api === 'function' ? api : undefined;
}
function evaluateConsent(disclosureId) {
    var _a, _b, _c;
    if (!initializedOptions)
        return false;
    if (!initializedOptions.enabled) {
        return true;
    }
    if (latestSource === 'fallback') {
        return (_a = initializedOptions.defaultConsent) !== null && _a !== void 0 ? _a : false;
    }
    const disclosure = initializedOptions.disclosures[disclosureId];
    if (!disclosure || !latestTcData) {
        debugLog('Consent denied: disclosure or tcData missing', {
            disclosureId,
            hasDisclosure: !!disclosure,
            hasTcData: !!latestTcData
        });
        return false;
    }
    const vendorId = initializedOptions.vendorId;
    if (!disclosure.purposeConsentIds.every(purposeId => { var _a, _b; return !!((_b = (_a = latestTcData.purpose) === null || _a === void 0 ? void 0 : _a.consents) === null || _b === void 0 ? void 0 : _b[purposeId]); })) {
        debugLog('Consent denied: missing required purpose consent', {
            disclosureId,
            requiredPurposeConsentIds: disclosure.purposeConsentIds
        });
        return false;
    }
    if (!((_c = (_b = latestTcData.vendor) === null || _b === void 0 ? void 0 : _b.consents) === null || _c === void 0 ? void 0 : _c[vendorId])) {
        debugLog('Consent denied: missing vendor consent', { disclosureId, vendorId });
        return false;
    }
    debugLog('Consent granted', { disclosureId });
    return true;
}
function emitToSubscribers() {
    subscribers.forEach(entry => {
        const allowed = evaluateConsent(entry.disclosureId);
        if (entry.lastAllowedState === allowed) {
            return;
        }
        entry.lastAllowedState = allowed;
        entry.onChange({
            allowed,
            tcData: latestTcData,
            source: latestSource
        });
    });
}
function subscribeInternal(options, onChange) {
    var _a;
    if (!(options === null || options === void 0 ? void 0 : options.enabled)) {
        debugLog('TCF disabled, allow-all mode active');
        onChange({ source: 'disabled' });
        return () => undefined;
    }
    const timeoutMs = (_a = options.timeoutMs) !== null && _a !== void 0 ? _a : DEFAULT_TIMEOUT_MS;
    let isDisposed = false;
    let listenerId;
    let timer;
    function emit(state) {
        if (isDisposed)
            return;
        onChange(state);
    }
    function startTimeout(source) {
        if (timer) {
            window.clearTimeout(timer);
        }
        timer = window.setTimeout(() => {
            debugLog(`TCF ${source} listener timed out, fallback active`);
            emit({ source: 'fallback' });
        }, timeoutMs);
    }
    function clearTimeoutIfRunning() {
        if (!timer)
            return;
        window.clearTimeout(timer);
        timer = undefined;
    }
    const tcfApi = getTcfApi();
    if (tcfApi) {
        startTimeout('direct');
        debugLog('Using direct __tcfapi transport');
        tcfApi('addEventListener', 2, (tcData, success) => {
            if (!success || !tcData) {
                clearTimeoutIfRunning();
                debugLog('Direct __tcfapi listener failed, fallback active');
                emit({ source: 'fallback' });
                return;
            }
            clearTimeoutIfRunning();
            if (typeof tcData.listenerId === 'number') {
                listenerId = tcData.listenerId;
            }
            debugLog('Received direct TCF update', { eventStatus: tcData.eventStatus, listenerId });
            emit({ tcData, source: 'tcf' });
        });
        return () => {
            isDisposed = true;
            clearTimeoutIfRunning();
            if (typeof listenerId === 'number') {
                tcfApi('removeEventListener', 2, () => undefined, listenerId);
                debugLog('Removed direct __tcfapi listener', { listenerId });
            }
        };
    }
    const tcfApiWindow = getTcfApiWindow();
    if (!tcfApiWindow) {
        debugLog('No __tcfapiLocator found, fallback active');
        emit({ source: 'fallback' });
        return () => undefined;
    }
    const callId = `trendads-tcf-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;
    startTimeout('postmessage');
    function onMessage(event) {
        var _a;
        const response = (_a = event.data) === null || _a === void 0 ? void 0 : _a.__tcfapiReturn;
        if (!response || response.callId !== callId) {
            return;
        }
        clearTimeoutIfRunning();
        if (!response.success || !response.returnValue) {
            debugLog('TCF postMessage response unsuccessful, activating fallback', { callId });
            emit({ source: 'fallback' });
            return;
        }
        const tcData = response.returnValue;
        if (typeof tcData.listenerId === 'number') {
            listenerId = tcData.listenerId;
        }
        debugLog('Received postMessage TCF update', { eventStatus: tcData.eventStatus, listenerId });
        emit({ tcData, source: 'tcf' });
    }
    window.addEventListener('message', onMessage);
    tcfApiWindow.postMessage({
        __tcfapiCall: {
            command: 'addEventListener',
            version: 2,
            callId
        }
    }, '*');
    debugLog('Registered postMessage TCF listener', { callId });
    return () => {
        isDisposed = true;
        clearTimeoutIfRunning();
        window.removeEventListener('message', onMessage);
        if (typeof listenerId === 'number') {
            tcfApiWindow.postMessage({
                __tcfapiCall: {
                    command: 'removeEventListener',
                    version: 2,
                    callId: `${callId}-remove`,
                    parameter: listenerId
                }
            }, '*');
            debugLog('Removed postMessage TCF listener', { listenerId });
        }
    };
}
export function init(options) {
    var _a;
    initializedOptions = options;
    latestTcData = undefined;
    latestSource = options.enabled ? 'tcf' : 'disabled';
    debugLog('Initialized TCF facade', {
        enabled: (_a = options.enabled) !== null && _a !== void 0 ? _a : false,
        disclosures: Object.keys(options.disclosures)
    });
    managerUnsubscribe === null || managerUnsubscribe === void 0 ? void 0 : managerUnsubscribe();
    managerUnsubscribe = subscribeInternal(options, state => {
        latestTcData = state.tcData;
        latestSource = state.source;
        emitToSubscribers();
    });
}
export function hasConsent(disclosureId) {
    return evaluateConsent(disclosureId);
}
export function subscribe(disclosureId, onChange) {
    const entry = { disclosureId, onChange };
    subscribers.add(entry);
    debugLog('Added disclosure subscriber', { disclosureId, subscriberCount: subscribers.size });
    emitToSubscribers();
    return () => {
        subscribers.delete(entry);
        debugLog('Removed disclosure subscriber', { disclosureId, subscriberCount: subscribers.size });
    };
}
//# sourceMappingURL=TCF.js.map